<template>
  <div class="employee-list">
    <div class="heading-background-container">
      <div class="container heading-container">
        <h1 class="style-h1">Vores medarbejdere</h1>
        <p class="style-h4">Få et overblik over alle medarbejderne på NEXT Uddannelse København samt deres kontaktinformationer.</p>
        <form @submit.prevent>
          <input
              type="text"
              placeholder="Søg på navn eller stilling"
              aria-label="Søg på navn eller stilling"
              v-model="searchQuery"
              @input="onSearchInput"
          >
          <select v-model="selectedDepartment" @change="onDepartmentFilterChange" aria-label="Filtrer efter afdeling">
            <option disabled hidden value="">Filtrer efter afdeling</option>
            <option value="reset">Ingen afdeling</option>
            <option v-for="department in allDepartments.filter(d => d.active)" :key="department.name">
              {{ department.name }}
            </option>
          </select>
        </form>
      </div>
    </div>
    <div class="container content-container">

      <div v-if="error" class="text-center text-danger">
        <p>Der skete en fejl ved indhentning af medarbejdere.</p>
      </div>

      <div>
        <div v-if="isSearchActive" class="search-results-summary">
          <p v-if="departments.length > 0">
            Fandt {{ totalEmployees }} medarbejder{{ totalEmployees !== 1 ? 'e' : '' }}
            <span v-if="searchQuery.trim() !== ''">
              for søgningen "{{ searchQuery }}"
            </span>
            <span v-if="selectedDepartment !== '' && selectedDepartment !== 'reset'">
              med afdelingsfilter: {{ selectedDepartment }}
            </span>
          </p>
          <p v-else>
            Ingen medarbejdere fundet
            <span v-if="searchQuery.trim() !== ''">
              for søgningen "{{ searchQuery }}"
            </span>
            <span v-if="selectedDepartment !== '' && selectedDepartment !== 'reset'">
              med afdelingsfilter: {{ selectedDepartment }}
            </span>
          </p>
          <button @click="clearSearch" class="button outlined">
            Vis alle medarbejdere
          </button>
        </div>

        <div v-if="totalEmployees > 0">
          <div
              v-for="department in activeDepartments"
              :key="department.id"
          >
            <h2 class="department-title style-h2">{{ department.name }}</h2>

            <div class="employee-container">
              <div
                  v-for="employee in department.activeEmployees"
                  :key="employee.id"
                  class="employee-info-container"
              >
                <h3 class="style-h3">{{ employee.name }}</h3>
                <div class="employee-info">
                  <p>{{ employee.title }}</p>

                  <p v-if="employee.workPhone">
                    Tlf:
                    <a :href="'tel:' + ensurePhoneValid(employee.workPhone)">
                      {{ formatPhone(employee.workPhone) }}
                    </a>
                  </p>

                  <p v-if="employee.mobilePhone">
                    Mobil:
                    <a :href="'tel:' + ensurePhoneValid(employee.mobilePhone)">
                      {{ formatPhone(employee.mobilePhone) }}
                    </a>
                  </p>

                  <p v-if="employee.email">
                    Mail:
                    <a :href="'mailto:' + employee.email">
                      {{ employee.email }}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading && departments.length === 0" class="text-center">
          <p>Henter medarbejdere...</p>
        </div>

        <div v-if="!isSearchActive">
          <!-- Infinite scroll trigger -->
          <div
              v-if="hasMore"
              ref="infiniteScrollTrigger"
              class="infinite-scroll-trigger"
          ></div>

          <!-- Loading indicator for additional pages -->
          <div v-if="loading && departments.length > 0" class="text-center">
            <p>Indlæser flere medarbejdere...</p>
          </div>

          <!-- End of results indicator -->
          <div v-if="!hasMore && departments.length > 0" class="text-center">
            <p>Alle medarbejdere er blevet indlæst</p>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'EmployeeList',
  data() {
    return {
      departments: [],
      allDepartments: [],
      loading: true,
      error: null,
      currentPage: 1,
      hasMore: true,
      searchQuery: '',
      selectedDepartment: "",
      searchTimeout: null,
      isSearchActive: false,
      totalEmployees: 0
    }
  },
  async mounted() {
    await this.fetchEmployees(1)
    this.setupInfiniteScroll()
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  computed: {
    activeDepartments() {
      if (!this.departments) return [];

      return this.departments
          .map(department => {
            const activeEmployees = department.employees?.filter(e => e.active) || [];
            return {
              ...department,
              activeEmployees, 
            };
          })
          .filter(department => department.activeEmployees.length > 0);
    },

    totalEmployees() {
      return this.activeDepartments.reduce(
          (sum, department) => sum + department.activeEmployees.length,
          0
      );
    },
  },
  methods: {
    async fetchEmployees(page, append = false) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(`/umbraco/api/Employee/GetEmployeesPaginated?page=${page}&minEmployees=50`)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (append) {
          this.departments.push(...data.departments)
          void this.$nextTick(() => {
            this.setupInfiniteScroll()
          })
        } else {
          this.departments = data.departments
        }
        this.allDepartments = data.allDepartments
        this.totalEmployees = data.totalEmployees
        this.hasMore = data.hasMore
        this.currentPage = page

      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async searchEmployees(query, selectedDepartment = null) {
      this.error = null
      this.isSearchActive = true

      if (this.observer) {
        this.observer.disconnect()
      }

      try {
        const encodedQuery = encodeURIComponent(query.trim())
        const encodedDepartment = encodeURIComponent(selectedDepartment.trim())
        const response = await fetch(`/umbraco/api/Employee/GetEmployeesSearch?searchQuery=${encodedQuery}&department=${encodedDepartment}`)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        this.departments = data.departments
        this.totalEmployees = data.totalResults
      } catch (err) {
        this.error = true
      }
    },

    onDepartmentFilterChange() {
      if (this.searchQuery.trim().length === 0 && this.selectedDepartment === "reset") {
        this.clearSearch()
        return;
      }

      this.performSearch()
    },

    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      if (this.searchQuery.trim().length === 0 && this.selectedDepartment === "reset") {
        this.clearSearch()
        return
      }

      if (this.searchQuery.trim().length === 0) {
        this.clearSearch()
        return;
      }

      // Debounce search - wait 300ms after user stops typing
      this.searchTimeout = setTimeout(() => {
        this.performSearch()
      }, 300)
    },

    async performSearch() {
      const query = this.searchQuery.trim()
      await this.searchEmployees(query, this.selectedDepartment)
    },

    async clearSearch() {
      this.searchQuery = ''
      this.isSearchActive = false
      this.totalResults = 0

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      await this.fetchEmployees(1)
      this.setupInfiniteScroll()
    },

    async loadNextPage() {
      if (this.hasMore && !this.loading) {
        await this.fetchEmployees(this.currentPage + 1, true)
      }
    },

    setupInfiniteScroll() {
      if (this.observer) {
        this.observer.disconnect()
      }

      // Wait for DOM to update
      this.$nextTick(() => {
        const trigger = this.$refs.infiniteScrollTrigger
        if (!trigger) return

        this.observer = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting && this.hasMore && !this.loading) {
                this.loadNextPage()
              }
            },
            {
              threshold: 0.1,
              rootMargin: '400px'
            }
        )

        this.observer.observe(trigger)
      })

    },
    formatPhone(phone) {
      phone = phone.trim();

      // If data is incomplete (missing + sign, we prepend it
      if (!phone.startsWith('+') && phone.startsWith('45') && phone.length === 10) {
        phone = '+' + phone;
      }

      if (phone.startsWith('+45')) {
        // Slice off the +45 prefix.
        let rest = phone.slice(3);
        let parts = [];

        while (rest.length > 0) {
          parts.push(rest.slice(0, 2));
          rest = rest.slice(2);
        }

        return '+45 ' + parts.join(' ');
      }

      return phone;
    },
    ensurePhoneValid(phone) {
      if (!phone.startsWith('+') && phone.startsWith('45') && phone.length === 10) {
        phone = '+' + phone;
      }

      return phone;
    }
  }
}
</script>